import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { socket } from "../mediasoup/socket";
import { recordingSocket } from "../mediasoup/recordingSocket";
import { useAuth } from "../../context/AuthProvider";
import { usePoster } from "../../hooks/usePoster";
import logger from "../../utils/logger";

import StreamCountdown from "../streaming/StreamCountdown";
import StreamStatusScreen from "../streaming/StreamStatusScreen";
import SecureStreamPlayer, {
  type StreamPlayerHandle,
} from "../streaming/StreamPlayer";
import api from "../../lib/api";

type StreamStatus =
  | "waiting"
  | "scheduled"
  | "live"
  | "paused"
  | "rec"
  | "ended";
type Mode = "CNR" | "COURTROOM" | null;

interface WatchedByInfo {
  sessionId: string;
  userId: string | undefined;
  ip: string;
  latitude: any;
  longitude: any;
  date: Date;
}

/** How long a "waiting" is treated as an encoder handoff, not a stop. */
const WAITING_GRACE_MS = 20000;
/** How long to wait for ffmpeg to publish index.m3u8 after a switch. */
const MANIFEST_WAIT_MS = 25000;
const MANIFEST_POLL_MS = 500;

const toFullPath = (raw?: string | null) => {
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  // raw is like /hls/xxx/index.m3u8 — prepend stream server
  return `${import.meta.env.VITE_APP_SERVER}${raw}`;
};

export default function UniversalPlayer() {
  const location = useLocation();
  const { courtRoomNo } = useParams();
  const { user, userLocation } = useAuth();
  const post = usePoster();

  const CNR = location.state?.cnr as string | undefined;
  const date = location.state?.date as string | undefined;
  const mode: Mode = courtRoomNo ? "COURTROOM" : CNR ? "CNR" : null;

  const sessionId = CNR || courtRoomNo || "";

  const [status, setStatus] = useState<StreamStatus>("waiting");
  const [playerStreamPath, setPlayerStreamPath] = useState<string | null>(null);
  const [startAt, setStartAt] = useState<string | null>(null);
  const [endAt, setEndAt] = useState<string | null>(null);
  const [courtName, setCourtName] = useState<string>("");
  const [watchedBy, setWatchedBy] = useState<WatchedByInfo | null>(null);
  const [switching, setSwitching] = useState(false);
  const [hlsToken, setHlsToken] = useState<string>("");

  const playerRef = useRef<StreamPlayerHandle>(null);
  const lastValidStreamPath = useRef<string | null>(null);
  const waitingTimerRef = useRef<any>(null);
  const probeRef = useRef(0);
  const hasRunRef = useRef(false);

  const ip = user?.ip || "0.0.0.0";
  const mobile = user?.phone || "";

  const isPaused = status === "paused";
  const isLive = status === "live";

  /* ---------------------------------------------------------
      PAUSE / PLAY ON STATUS CHANGE
  --------------------------------------------------------- */
  useEffect(() => {
    if (!playerRef.current) return;
    if (isPaused) {
      playerRef.current.pause(); // also mutes → kills audio leak
    } else if (isLive) {
      playerRef.current.play(); // unmutes + seeks to live edge
    }
  }, [isPaused, isLive]);

  /* ---------------------------------------------------------
      FORCE PLAY ON A NEW SOURCE
      status stays "live" across a switch, so the effect above
      never re-fires — this one does.
  --------------------------------------------------------- */
  useEffect(() => {
    if (!playerStreamPath || !isLive || isPaused) return;
    const t = setTimeout(() => playerRef.current?.play(), 300);
    return () => clearTimeout(t);
  }, [playerStreamPath, isLive, isPaused]);

  /* ---------------------------------------------------------
      BUILD WATCHED-BY PAYLOAD
  --------------------------------------------------------- */
  useEffect(() => {
    if (hasRunRef.current || !user || !sessionId || !ip || !userLocation) return;
    setWatchedBy({
      sessionId,
      userId: user.id,
      ip,
      latitude: userLocation.lat,
      longitude: userLocation.lon,
      date: date ? new Date(date) : new Date(),
    });
    hasRunRef.current = true;
  }, [user, sessionId, ip, userLocation, date]);

  /* ---------------------------------------------------------
      MANIFEST PROBE
      ffmpeg writes the playlist header before the first segment
      closes, so a bare 200 is not enough — wait for #EXTINF.
      Never hand the player a URL that would 404 or parse empty.
  --------------------------------------------------------- */
// Add ref for token in probe
const hlsTokenRef = useRef(hlsToken);
useEffect(() => {
  hlsTokenRef.current = hlsToken;
}, [hlsToken]);

const waitForManifest = useCallback(async (url: string) => {
  const token = ++probeRef.current;
  const deadline = Date.now() + MANIFEST_WAIT_MS;

  while (Date.now() < deadline) {
    if (probeRef.current !== token) return false;
    try {
      const currentToken = hlsTokenRef.current;
      const probeUrl = currentToken && !url.includes("token=")
        ? `${url}${url.includes("?") ? "&" : "?"}token=${currentToken}`
        : url;

      const r = await fetch(probeUrl, { method: "GET", cache: "no-store" });
      if (r.ok) {
        const text = await r.text();
        if (text.includes("#EXTINF")) return probeRef.current === token;
      }
    } catch { /* not published yet */ }
    await new Promise((res) => setTimeout(res, MANIFEST_POLL_MS));
  }
  return false;
}, []); 

  const adoptUrl = useCallback(
    async (raw?: string | null) => {
      const full = toFullPath(raw);
      if (!full) return;
      if (full === lastValidStreamPath.current) return;

      setSwitching(true);
      const ready = await waitForManifest(full);
      if (!ready) {
        setSwitching(false);
        return;
      }

      lastValidStreamPath.current = full;
      setPlayerStreamPath(full);
      setSwitching(false);
      console.log("🔀 adopted stream path:", full);
    },
    [waitForManifest],
  );

  const clearWaitingTimer = () => {
    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
  };

  const hardStop = useCallback((next: StreamStatus) => {
    probeRef.current += 1; // cancel any in-flight probe
    lastValidStreamPath.current = null;
    setPlayerStreamPath(null);
    setSwitching(false);
    setStatus(next);
  }, []);

  /* ---------------------------------------------------------
      CNR MODE (API)
  --------------------------------------------------------- */
  const fetchCNRStream = useCallback(async () => {
    if (!CNR) return;
    try {
      const res = await post(`/api/search/streaming-status`, { cnr: CNR });
      logger.log("📡 Stream status response:", res);

      const d = res?.data;
      if (!d) return;

      if (!d.streamStatus) {
        setStatus("waiting");
        return;
      }

      void adoptUrl(d.streamPath);
      setStartAt(d.streamStartAt || null);
      setEndAt(d.streamEndAt || null);
      setCourtName(d.highCourtName || d.courtName || "");

      if (d.streamingStatus === "paused") {
        setStatus("paused");
        return;
      }
      if (!d.streamStartAt) {
        setStatus("waiting");
        return;
      }

      const now = Date.now();
      const start = new Date(d.streamStartAt).getTime();
      const end = d.streamEndAt ? new Date(d.streamEndAt).getTime() : Infinity;

      if (now < start) setStatus("scheduled");
      else if (now <= end) setStatus("live");
      else setStatus("ended");
    } catch (err) {
      logger.error("❌ Error fetching stream:", err);
    }
  }, [CNR, post, adoptUrl]);

  /* ---------------------------------------------------------
      COURTROOM MODE (Socket)

      Rules learned the hard way:
      - ANY event carrying an hlsUrl wins, including "switching" —
        stream-backend announces the new manifest there and does not
        always repeat it on the following "live".
      - "waiting" arrives BEFORE "switching" during a handoff, so it
        must not clear the path immediately.
      - a url-less "live" mid-handoff keeps whatever we already have.
  --------------------------------------------------------- */
  const handleCourtroomUpdate = useCallback(
    (data: any) => {
      if (data.courtRoomNo !== courtRoomNo) return;
      if (data.visibleAt) setStartAt(new Date(data.visibleAt).toISOString());
      if (data.courtName) setCourtName(data.courtName);

      if (data.status !== "waiting") clearWaitingTimer();

      // adopt first, regardless of status — probe runs in the background
      if (data.hlsUrl) void adoptUrl(data.hlsUrl);

      switch (data.status) {
        case "switching":
          // keep the player mounted and live; old stream plays until
          // the new manifest is confirmed ready
          setStatus("live");
          return;

        case "live":
          if (!data.hlsUrl && lastValidStreamPath.current) {
            setPlayerStreamPath(lastValidStreamPath.current);
          }
          setStatus("live");
          return;

        case "paused":
          setStatus("paused");
          return;

        case "rec":
          hardStop("rec");
          return;

        case "waiting":
          // encoder handoff, not a stop — arm a timeout instead of
          // tearing the player down straight away
          if (waitingTimerRef.current) return;
          waitingTimerRef.current = setTimeout(() => {
            waitingTimerRef.current = null;
            hardStop("waiting");
          }, WAITING_GRACE_MS);
          return;

        case "ended":
          hardStop("ended");
          return;

        case "scheduled":
          setStatus("scheduled");
          return;

        default:
          setStatus(data.status);
      }
    },
    [courtRoomNo, adoptUrl, hardStop],
  );

  /* ---------------------------------------------------------
      SOCKET STREAM EVENTS
  --------------------------------------------------------- */
  useEffect(() => {
    if (mode === "CNR") {
      fetchCNRStream();

      const onStatus = (data: any) => {
        if (data?.sessionId && data.sessionId !== CNR) return;

        if (data?.status === "paused") {
          setStatus("paused");
          return;
        }
        if (data?.status === "live" && data?.streamingStatus === "live") {
          setStatus("live");
          return;
        }
        fetchCNRStream();
      };

      const onStop = (data: any) => {
        if (data?.sessionId && data.sessionId !== CNR) return;
        hardStop("ended");
      };

      recordingSocket.on("stream-status", onStatus);
      recordingSocket.on("stream_stop", onStop);

      return () => {
        recordingSocket.off("stream-status", onStatus);
        recordingSocket.off("stream_stop", onStop);
      };
    }

    if (mode === "COURTROOM") {
      recordingSocket.emit("watch_courtroom", { courtRoomNo });
      recordingSocket.on("courtroom_stream_update", handleCourtroomUpdate);

      return () => {
        recordingSocket.off("courtroom_stream_update", handleCourtroomUpdate);
        clearWaitingTimer();
      };
    }
  }, [
    mode,
    courtRoomNo,
    CNR,
    fetchCNRStream,
    handleCourtroomUpdate,
    hardStop,
  ]);

  // CNR: poll every 15s
  useEffect(() => {
    if (mode !== "CNR") return;
    const interval = setInterval(fetchCNRStream, 15000);
    return () => clearInterval(interval);
  }, [mode, fetchCNRStream]);

  /* ---------------------------------------------------------
      STREAM TIMER — CNR only. Courtroom status is socket-driven;
      a local clock would fight the switching flow.
  --------------------------------------------------------- */
  useEffect(() => {
    if (mode !== "CNR") return;
    if (!startAt || !endAt) return;

    const start = new Date(startAt).getTime();
    const end = new Date(endAt).getTime();

    const check = () => {
      const now = Date.now();
      setStatus((prev) => {
        if (prev === "paused" || prev === "rec") return prev;
        if (now >= end) return "ended";
        if (now >= start) return "live";
        return "scheduled";
      });
    };

    check();
    const i = setInterval(check, 2000);
    return () => clearInterval(i);
  }, [mode, startAt, endAt]);

  /* ---------------------------------------------------------
      WATCHED-BY / LEFT-BY
  --------------------------------------------------------- */
  useEffect(() => {
    if (!socket || !isLive || !watchedBy) return;
    socket.emit("watchedBy", watchedBy);
  }, [isLive, watchedBy]);

  useEffect(() => {
    if (!socket || !sessionId || !user?.id) return;
    const payload = { sessionId, userId: user.id };
    const handleUnload = () => socket.emit("leftBy", payload);
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [sessionId, user]);

  useEffect(() => {
    return () => {
      if (socket && sessionId && user?.id) {
        socket.emit("leftBy", { sessionId, userId: user.id });
        logger.log("🚪 LEFT BY SENT ON ROUTE CHANGE");
      }
    };
  }, [sessionId, user]);


  //fetch token 
  useEffect(() => {
  if (!user?.id) return;

  const fetchToken = () => {
    api.get("/api/search/stream/hls-token")
      .then((res) => setHlsToken(res.data.token))
      .catch(console.error);
  };

  fetchToken();
  const interval = setInterval(fetchToken, 30 * 60 * 1000);
  return () => clearInterval(interval);
}, [user?.id]);

  /* ---------------------------------------------------------
      OVERLAY
      "paused" is absent on purpose — the player renders its own
      pause overlay from the isPaused prop.
  --------------------------------------------------------- */
  const statusScreenLabels = {
    courtLabel: courtRoomNo ? `Court hall ${courtRoomNo}` : undefined,
    matterLabel: CNR ? `CNR: ${CNR}` : undefined,
  };

  const overlay = () => {
    if (!mode)
      return <StreamStatusScreen variant="overlay" status="unavailable" />;
    if (status === "waiting")
      return (
        <StreamStatusScreen
          variant="overlay"
          status="notStarted"
          {...statusScreenLabels}
        />
      );
    if (status === "ended")
      return (
        <StreamStatusScreen
          variant="overlay"
          status="concluded"
          {...statusScreenLabels}
        />
      );

    if (status === "rec")
      return (
        <StreamStatusScreen
          variant="overlay"
          status="notStreaming"
          {...statusScreenLabels}
        />
      );

    if (status === "scheduled")
      return startAt ? (
        <StreamCountdown
          variant="overlay"
          streamStartAt={startAt}
          onCountdownEnd={() => setStatus("live")}
          {...statusScreenLabels}
        />
      ) : (
        <StreamStatusScreen
          variant="overlay"
          status="notStarted"
          {...statusScreenLabels}
        />
      );

    // live but nothing to play yet (first load, or waiting on a new manifest)
    if (status === "live" && !playerStreamPath)
      return (
        <StreamStatusScreen
          variant="overlay"
          status={switching ? "switching" : "loading"}
          {...statusScreenLabels}
        />
      );

    return null;
  };

  const currentOverlay = overlay();
  
  /* ---------------------------------------------------------
      RENDER — player always mounted, overlays on top
  --------------------------------------------------------- */
return (
  <div className="relative h-full w-full">
    {status !== "scheduled" && (
      <SecureStreamPlayer
        key="stable-player"
        ref={playerRef}
        streamPath={playerStreamPath || ""}
         hlsToken={hlsToken} 
        user={user?.name || ""}
        ip={ip}
        caseId={sessionId}
        mobile={mobile}
        courtName={courtName}
        location={user?.location || ""}
        isPaused={isPaused}
      />
    )}

    {currentOverlay && (
      <div className="absolute inset-0 z-20 bg-black">
        {currentOverlay}
      </div>
    )}
  </div>
);
}